import { format } from 'date-fns';
import Stars from '@quno/patient-journey/src/components/ui/Stars';
import RichText from '@quno/patient-journey/src/components/ui/ContentBlocks/RichText';
import { useTemplateWords } from '@quno/patient-journey/src/hooks/useTemplateWords';
import { Card } from '@quno/patient-journey/src/components/Card/Card';
import { useEffect, useState } from 'react';
import { getRichTextMultilocale } from '@quno/onboarding/src/generator/configs/componentMapper';
import { BLOCKS, type Document } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { richTextWithoutParagraphConfig } from '@quno/patient-journey/src/contentful/patient-journey/richTextWithoutParagraphConfig';
import styles from '@quno/patient-journey/src/components/Card/Variants/ReviewCard.module.scss';
import type { StringAttribute } from '@quno/onboarding/src/generator/types/generics/ModelAttributes';
import type { FormLocales } from '@quno/onboarding/src/constants/locales';
import type { CardProps } from '@quno/patient-journey/src/components/Card/card.config';

export type ReviewCardProps = {
  rating?: number | null;
  date: string;
  title: StringAttribute;
  text: StringAttribute;
  footer?: string | null;
  locale: FormLocales;
};

export const ReviewCard = ({
  text,
  rating = 0,
  date,
  title,
  footer,
  backgroundColorOverwrite,
  darkModeEnabled,
  fontColorOverride,
  cardBorderRadius,
  locale,
}: Omit<CardProps, 'text'> & ReviewCardProps): JSX.Element => {
  const { replaceTemplateWords } = useTemplateWords();
  const [state, setState] = useState<{
    title: Document | undefined;
    text: Document | undefined;
  }>();

  useEffect(() => {
    const run = async (): Promise<void> => {
      const [titleContent, textContent] = await Promise.all([
        getRichTextMultilocale(title, locale, (text) =>
          replaceTemplateWords(text),
        ),
        getRichTextMultilocale(text, locale, (text) =>
          replaceTemplateWords(text),
        ),
      ]);
      setState({
        title: titleContent,
        text: textContent,
      });
    };

    void run();
  }, [title, text, locale]);

  return (
    <Card
      className={styles.item}
      backgroundColorOverwrite={backgroundColorOverwrite}
      darkModeEnabled={darkModeEnabled}
      fontColorOverride={fontColorOverride}
      style={{ borderRadius: cardBorderRadius || 0 }}
    >
      <div className={styles.meta}>
        <div>
          <Stars rating={parseFloat((rating || 0).toFixed(1))} />
        </div>
        <small className={styles.date}>
          {!!date && (
            <time dateTime={date}>
              {format(new Date(date), 'LLLL d, yyyy')}
            </time>
          )}
        </small>
      </div>
      <h4
        className={styles.title}
        style={{ color: fontColorOverride || undefined }}
      >
        {documentToReactComponents(
          state?.title || {
            content: [],
            nodeType: BLOCKS.DOCUMENT,
            data: {},
          },
          richTextWithoutParagraphConfig,
        )}
      </h4>
      <p
        className={styles.customer}
        style={{ color: fontColorOverride || undefined }}
      >
        {footer}
      </p>
      <div>
        <RichText fields={state?.text} noWrapper />
      </div>
    </Card>
  );
};
